import React, { useRef, useState, useCallback, useEffect } from 'react'

import { Calendar, Plus, Search, X } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

import { Pagination } from '@/core/api/dtos/common/metaPagination'
import { Schedule } from '@/core/api/entities/schedule'
import { useTranslation } from '@/core/i18n/useTranslation'
import { Button } from '@/shared/components/atoms/Button'
import Icon from '@/shared/components/atoms/Icon'
import { Input } from '@/shared/components/atoms/Input'
import { Label } from '@/shared/components/atoms/Label/Label'
import { NavButton } from '@/shared/components/atoms/NavButton'
import { Skeleton } from '@/shared/components/atoms/Skeleton'
import { Textarea } from '@/shared/components/atoms/TextArea'
import DesktopOnlyModal from '@/shared/components/molecules/DesktopOnlyModal/DesktopOnlyModal'
import {
	Dialog,
	DialogContent,
	DialogTitle,
} from '@/shared/components/molecules/Dialog/dialog'
import { PageLayout } from '@/shared/components/organisms/Layout/PageLayout/PageLayout'

import { AssignScheduleModal } from './components/AssignScheduleModal'
import { DuplicateScheduleModal } from './components/DuplicateScheduleModal'
import FilteredScheduleSection from './components/FilteredScheduleSection'
import { RemoveScheduleModal } from './components/RemoveScheduleModal'
import { ScheduleCard } from './components/ScheduleCard'
import ScheduleCarousel, {
	ScheduleCarouselRef,
} from './components/ScheduleCarousel'
import ScheduleSkeleton from './components/ScheduleSkeleton'
import { FilteredSection } from './hooks/useFilteredScheduleSections'

// Types
interface SchedulesListPageUIProps {
	// Loading states
	isInitialLoading?: boolean
	isLoadingInUse: boolean
	isLoadingAssigned: boolean
	isLoadingFavorites: boolean
	// Search
	searchQuery: string
	onSearchChange: (query: string) => void
	showSearchResults: boolean
	isLoadingSearch: boolean
	searchResults: Schedule[]
	// In-use schedules
	inUseSchedules: Schedule[]
	inUsePagination?: Pagination
	onInUsePageChange?: (direction: 'next' | 'prev') => void
	// Assigned schedules
	assignedSchedules: Schedule[]
	assignedPagination?: Pagination
	onAssignedPageChange?: (direction: 'next' | 'prev') => void
	// Favorite schedules
	favoriteSchedules: Schedule[]
	favoritesPagination?: Pagination
	onFavoritesPageChange?: (direction: 'next' | 'prev') => void
	favoriteScheduleIds?: Set<number>
	// Filtered sections (business categories)
	filteredSections: FilteredSection[]
	hasMoreFilteredSections: boolean
	onLoadMoreFilteredSections: () => void
	// Schedule actions
	onScheduleClick: (schedule: Schedule) => void
	onFavoriteClick?: (schedule: Schedule, isFavoritesCarousel: boolean) => void
	loadingScheduleId?: number | null
	onAssign: (scheduleId: number, name: string) => void
	onDuplicate?: (scheduleId: number, name: string, description: string) => void
	onEdit?: (scheduleId: number) => void
	onRemove?: (scheduleId: number, name: string, description: string) => void
	// Create modal
	canCreateSchedule?: boolean
	isCreateModalOpen: boolean
	onOpenCreateModal: () => void
	onCloseCreateModal: () => void
	scheduleName: string
	onScheduleNameChange: (name: string) => void
	scheduleDescription: string
	onScheduleDescriptionChange: (description: string) => void
	editingScheduleId: string | null
	isSubmittingSchedule: boolean
	onCreateSchedule: () => void
	onCancelCreate: () => void
	// Assign modal
	isAssignModalOpen: boolean
	onAssignModalChange: (open: boolean) => void
	selectedScheduleForAssign: { id: string; name: string } | null
	// Remove modal
	isRemoveModalOpen: boolean
	onRemoveModalChange: (open: boolean) => void
	selectedScheduleForRemove: {
		id: string
		name: string
		description?: string
	} | null
	// Duplicate modal
	isDuplicateModalOpen: boolean
	onDuplicateModalChange: (open: boolean) => void
	selectedScheduleForDuplicate: {
		id: string
		name: string
		description?: string
	} | null
}

interface ScrollState {
	canScrollLeft: boolean
	canScrollRight: boolean
}

interface CarouselSectionProps {
	title: string
	titleIcon?: React.ReactNode
	schedules: Schedule[]
	isLoading: boolean
	carouselRef: React.RefObject<ScheduleCarouselRef | null>
	scrollState: ScrollState
	onScrollStateChange: (canScrollLeft: boolean, canScrollRight: boolean) => void
	onScheduleClick: (schedule: Schedule) => void
	onFavoriteClick?: (schedule: Schedule, isFavoritesCarousel: boolean) => void
	loadingScheduleId?: number | null
	isFavoritesCarousel?: boolean
	onPageChange?: (direction: 'next' | 'prev') => void
	pagination?: Pagination
	favoriteScheduleIds?: Set<number>
	showActions?: boolean
	onAssign?: (scheduleId: number, name: string) => void
	onDuplicate?: (scheduleId: number, name: string, description: string) => void
	onEdit?: (scheduleId: number) => void
	onRemove?: (scheduleId: number, name: string, description: string) => void
	viewAllUrl?: string
}

interface CarouselNavigationProps {
	carouselRef: React.RefObject<ScheduleCarouselRef | null>
	scrollState: ScrollState
	onPageChange?: (direction: 'next' | 'prev') => void
	pagination?: Pagination
}

// Constants
const DEFAULT_SKELETON_COUNT = 5

// Helper Components
const CarouselNavigationButtons: React.FC<CarouselNavigationProps> = ({
	carouselRef,
	scrollState,
	onPageChange,
	pagination,
}) => {
	const handleScrollRight = useCallback(() => {
		if (onPageChange && pagination) {
			if (pagination.total_pages > 1) {
				onPageChange('next')
			}
		} else {
			carouselRef.current?.scrollRight()
		}
	}, [carouselRef, onPageChange, pagination])

	const handleScrollLeft = useCallback(() => {
		if (onPageChange && pagination) {
			if (pagination.total_pages > 1) {
				onPageChange('prev')
			}
		} else {
			carouselRef.current?.scrollLeft()
		}
	}, [carouselRef, onPageChange, pagination])

	const canGoLeft = pagination
		? pagination.total_pages > 1
		: scrollState.canScrollLeft
	const canGoRight = pagination
		? pagination.total_pages > 1
		: scrollState.canScrollRight

	return (
		<div className="flex items-center gap-2">
			<NavButton onClick={handleScrollLeft} disabled={!canGoLeft}>
				<Icon name="chevron-left" width="16px" height="16px" color="white" />
			</NavButton>
			<NavButton onClick={handleScrollRight} disabled={!canGoRight}>
				<Icon name="chevron-right" width="16px" height="16px" color="white" />
			</NavButton>
		</div>
	)
}

const CarouselSection: React.FC<CarouselSectionProps> = ({
	title,
	titleIcon,
	schedules,
	isLoading,
	carouselRef,
	scrollState,
	onScrollStateChange,
	onScheduleClick,
	onFavoriteClick,
	isFavoritesCarousel = false,
	onPageChange,
	pagination,
	showActions = true,
	onAssign,
	onDuplicate,
	onEdit,
	onRemove,
	viewAllUrl,
}) => {
	const { t } = useTranslation('common')
	const navigate = useNavigate()

	// Don't hide section if loading - show skeleton instead
	if (schedules.length === 0 && !isLoading) {
		return null
	}

	const handleViewAllClick = () => {
		if (viewAllUrl) {
			navigate(viewAllUrl)
		}
	}

	return (
		<div className="mb-8">
			{/* Section Header */}
			<div className="flex w-full items-center justify-between md:mb-2">
				<h2 className="flex w-full items-baseline justify-between md:justify-start gap-4 m-0 text-foreground md:text-2xl text-lg font-medium md:font-normal leading-[130%]">
					{titleIcon}
					{title}
					<button
						onClick={handleViewAllClick}
						className="flex items-end bg-transparent border-none text-foreground opacity-90 text-xs font-medium cursor-pointer transition-opacity duration-200 underline decoration-muted-foreground underline-offset-2 p-0 m-0 hover:opacity-100 hover:text-foreground"
					>
						{t('entities.music.view_all')}
					</button>
				</h2>
				<div className="hidden md:block">
					<CarouselNavigationButtons
						carouselRef={carouselRef}
						scrollState={scrollState}
						onPageChange={onPageChange}
						pagination={pagination}
					/>
				</div>
			</div>
			{isLoading ? (
				<ScheduleSkeleton count={DEFAULT_SKELETON_COUNT} />
			) : (
				<ScheduleCarousel
					ref={carouselRef}
					schedules={schedules}
					onScheduleClick={onScheduleClick}
					onFavoriteClick={
						onFavoriteClick
							? (schedule) => onFavoriteClick(schedule, isFavoritesCarousel)
							: undefined
					}
					isLoading={isLoading}
					onScrollStateChange={onScrollStateChange}
					showActions={showActions}
					onAssign={onAssign}
					onDuplicate={onDuplicate}
					onEdit={onEdit}
					onRemove={onRemove}
				/>
			)}
		</div>
	)
}

// Search Results Section
interface SearchResultsSectionProps {
	searchResults: Schedule[]
	onScheduleClick: (schedule: Schedule) => void
	onFavoriteClick?: (schedule: Schedule, isFavoritesCarousel: boolean) => void
	loadingScheduleId?: number | null
	favoriteScheduleIds?: Set<number>
	onAssign: (scheduleId: number, name: string) => void
	onDuplicate?: (scheduleId: number, name: string, description: string) => void
	onEdit?: (scheduleId: number) => void
	onRemove?: (scheduleId: number, name: string, description: string) => void
	isLoading: boolean
}

const SearchResultsSection: React.FC<SearchResultsSectionProps> = ({
	searchResults,
	onScheduleClick,
	onAssign,
	onDuplicate,
	onEdit,
	onRemove,
	isLoading,
}) => {
	const { t } = useTranslation('common')

	return (
		<div className="mb-8">
			{/* Section Header */}
			<div className="flex items-center justify-between mb-6">
				<h2 className="flex items-baseline gap-4 m-0 text-foreground text-2xl font-semibold leading-[130%]">
					{t('entities.music.search_results')}
				</h2>
			</div>
			{isLoading ? (
				<ScheduleSkeleton
					count={5}
					className="flex-wrap !overflow-visible !py-0 mb-8"
				/>
			) : searchResults.length === 0 ? (
				<div className="flex items-center justify-center px-4 py-10 min-h-[300px]">
					<div className="flex flex-col items-center text-center max-w-[280px]">
						<div className="w-25 h-25 rounded-xl bg-muted/70 flex items-center justify-center mb-5">
							<Search className="h-10 w-10 text-foreground/90" />
						</div>
						<h3 className="text-lg font-semibold text-foreground m-0 mb-2">
							{t('entities.schedule.no_schedules_found')}
						</h3>
						<p className="text-xs leading-5 text-muted-foreground m-0">
							{t('entities.schedule.try_adjusting_search')}
						</p>
					</div>
				</div>
			) : (
				<div className="flex flex-wrap gap-4 mb-8">
					{searchResults.map((schedule) => (
						<ScheduleCard
							key={schedule.id}
							className="lg:m-0"
							schedule={schedule}
							showActions
							onClick={onScheduleClick}
							onAssign={onAssign}
							onDuplicate={onDuplicate}
							onEdit={onEdit}
							onRemove={onRemove}
						/>
					))}
				</div>
			)}
		</div>
	)
}

// Main Component
const SchedulesListPageUI: React.FC<SchedulesListPageUIProps> = ({
	isInitialLoading = false,
	isLoadingInUse,
	isLoadingAssigned,
	isLoadingFavorites,
	searchQuery,
	onSearchChange,
	showSearchResults,
	isLoadingSearch,
	searchResults,
	inUseSchedules,
	inUsePagination,
	onInUsePageChange,
	assignedSchedules,
	assignedPagination,
	onAssignedPageChange,
	favoriteSchedules,
	favoritesPagination,
	onFavoritesPageChange,
	favoriteScheduleIds,
	filteredSections,
	hasMoreFilteredSections,
	onLoadMoreFilteredSections,
	onScheduleClick,
	onFavoriteClick,
	loadingScheduleId,
	onAssign,
	onDuplicate,
	onEdit,
	onRemove,
	canCreateSchedule = true,
	isCreateModalOpen,
	onOpenCreateModal,
	onCloseCreateModal,
	scheduleName,
	onScheduleNameChange,
	scheduleDescription,
	onScheduleDescriptionChange,
	editingScheduleId,
	isSubmittingSchedule,
	onCreateSchedule,
	onCancelCreate,
	isAssignModalOpen,
	onAssignModalChange,
	selectedScheduleForAssign,
	isRemoveModalOpen,
	onRemoveModalChange,
	selectedScheduleForRemove,
	isDuplicateModalOpen,
	onDuplicateModalChange,
	selectedScheduleForDuplicate,
}) => {
	const { t } = useTranslation('common')
	const observerTarget = useRef<HTMLDivElement>(null)

	// Track loading states of filtered sections
	const [loadingSectionsCount, setLoadingSectionsCount] = useState(0)
	const loadingSectionsRef = useRef<Set<string>>(new Set())
	const [showDesktopOnlyWarning, setShowDesktopOnlyWarning] = useState(false)

	// Carousel refs
	const inUseCarouselRef = useRef<ScheduleCarouselRef>(null)
	const assignedCarouselRef = useRef<ScheduleCarouselRef>(null)
	const favoritesCarouselRef = useRef<ScheduleCarouselRef>(null)

	// Scroll states
	const [inUseScrollState, setInUseScrollState] = useState<ScrollState>({
		canScrollLeft: false,
		canScrollRight: true,
	})
	const [assignedScrollState, setAssignedScrollState] = useState<ScrollState>({
		canScrollLeft: false,
		canScrollRight: true,
	})
	const [favoritesScrollState, setFavoritesScrollState] = useState<ScrollState>(
		{
			canScrollLeft: false,
			canScrollRight: true,
		}
	)

	// Handle loading state changes from filtered sections
	const handleSectionLoadingChange = useCallback(
		(sectionId: string, isLoading: boolean) => {
			if (isLoading) {
				loadingSectionsRef.current.add(sectionId)
			} else {
				loadingSectionsRef.current.delete(sectionId)
			}
			setLoadingSectionsCount(loadingSectionsRef.current.size)
		},
		[]
	)

	// Scroll state change handlers
	const handleInUseScrollStateChange = useCallback(
		(canScrollLeft: boolean, canScrollRight: boolean) => {
			setInUseScrollState({ canScrollLeft, canScrollRight })
		},
		[]
	)

	const handleAssignedScrollStateChange = useCallback(
		(canScrollLeft: boolean, canScrollRight: boolean) => {
			setAssignedScrollState({ canScrollLeft, canScrollRight })
		},
		[]
	)

	const handleFavoritesScrollStateChange = useCallback(
		(canScrollLeft: boolean, canScrollRight: boolean) => {
			setFavoritesScrollState({ canScrollLeft, canScrollRight })
		},
		[]
	)

	const handleCreateSchedule = () => {
		if (window.matchMedia('(max-width: 1023px)').matches) {
			setShowDesktopOnlyWarning(true)
		} else {
			onOpenCreateModal()
		}
	}

	// Infinite scroll observer for filtered sections
	useEffect(() => {
		if (showSearchResults || !hasMoreFilteredSections) {
			return
		}

		let observer: IntersectionObserver | null = null
		let currentTarget: HTMLDivElement | null = null

		const timeoutId = setTimeout(() => {
			observer = new IntersectionObserver(
				(entries) => {
					const entry = entries[0]
					// Only load more if less than 3 sections are currently loading
					if (
						entry.isIntersecting &&
						hasMoreFilteredSections &&
						loadingSectionsCount < 3
					) {
						onLoadMoreFilteredSections()
					}
				},
				{
					threshold: 0,
					rootMargin: '200px',
				}
			)

			currentTarget = observerTarget.current
			if (currentTarget) {
				observer.observe(currentTarget)
			}
		}, 100)

		return () => {
			clearTimeout(timeoutId)
			if (observer && currentTarget) {
				observer.unobserve(currentTarget)
			}
		}
	}, [
		hasMoreFilteredSections,
		onLoadMoreFilteredSections,
		showSearchResults,
		loadingSectionsCount,
	])

	// Check if there are no schedules at all
	const hasNoSchedules =
		inUseSchedules.length === 0 &&
		assignedSchedules.length === 0 &&
		favoriteSchedules.length === 0

	if (isInitialLoading) {
		return (
			<PageLayout
				topAppBarLeftContent={
					<h1 className="md:hidden text-2xl font-semibold tracking-tight">
						{t('navigation.schedule')}
					</h1>
				}
				customHeader={
					<div className="hidden md:flex flex-col sm:flex-row w-full items-stretch sm:items-center gap-4 justify-between">
						<div className="space-y-0">
							<h2 className="flex items-center text-2xl font-normal tracking-tight md:text-3xl">
								{t('navigation.schedule')}
							</h2>
						</div>
					</div>
				}
			>
				<div className="mb-8 md:hidden">
					<Skeleton className="h-10 w-full rounded-full" />
				</div>

				<div className="mb-8">
					<Skeleton className="hidden md:block h-5 lg:h-6 w-32 lg:w-40 mb-4" />
					<ScheduleSkeleton count={DEFAULT_SKELETON_COUNT} />
				</div>

				<div className="mb-8">
					<Skeleton className="h-5 lg:h-6 w-32 lg:w-40 mb-4" />
					<ScheduleSkeleton count={DEFAULT_SKELETON_COUNT} />
				</div>
			</PageLayout>
		)
	}

	return (
		<>
			<PageLayout
				topAppBarLeftContent={
					<h1 className="md:hidden text-2xl font-semibold tracking-tight">
						{t('navigation.schedule')}
					</h1>
				}
				customHeader={
					<div className="hidden md:flex items-center justify-between">
						<div className="space-y-0">
							<h2 className="flex items-center text-2xl font-normal tracking-tight md:text-3xl">
								{t('navigation.schedule')}
							</h2>
						</div>
					</div>
				}
			>
				<div className="space-y-6 pb-24 md:pb-0">
					<div className="flex flex-col mb-8 md:flex-row md:items-center justify-between gap-4 lg:h-8">
						<div className="w-full relative flex-1 md:max-w-md">
							<Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
							<Input
								type="search"
								placeholder={t('entities.schedule.search_placeholder')}
								value={searchQuery}
								onChange={(e) => onSearchChange(e.target.value)}
								className="pl-9 pr-9"
							/>
							{searchQuery.trim().length > 0 && (
								<button
									type="button"
									onClick={() => onSearchChange('')}
									className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1 text-muted-foreground transition-colors hover:text-foreground"
									aria-label={t('actions.clear')}
								>
									<X className="h-4 w-4" />
								</button>
							)}
						</div>
						{canCreateSchedule && (
							<Button
								variant="action"
								size="action"
								className="hidden md:flex"
								onClick={handleCreateSchedule}
							>
								<Calendar className="mr-1.5 !h-4 !w-4" />
								{t('entities.schedule.create_schedule')}
							</Button>
						)}
					</div>

					{showSearchResults ? (
						<SearchResultsSection
							searchResults={searchResults}
							onScheduleClick={onScheduleClick}
							onFavoriteClick={onFavoriteClick}
							loadingScheduleId={loadingScheduleId}
							favoriteScheduleIds={favoriteScheduleIds}
							onAssign={onAssign}
							onDuplicate={onDuplicate}
							onEdit={onEdit}
							onRemove={onRemove}
							isLoading={isLoadingSearch}
						/>
					) : (
						<>
							{/* Empty state when no schedules */}
							{!isLoadingInUse &&
								!isLoadingAssigned &&
								!isLoadingFavorites &&
								hasNoSchedules && (
									<div className="p-4 md:p-6 border rounded-xl border-dashed bg-card">
										<div className="flex items-center gap-3 md:gap-4">
											<div className="flex h-10 w-10 md:h-12 md:w-12 items-center justify-center rounded-lg bg-primary/10">
												<Calendar className=" h-6 w-6 text-primary" />
											</div>
											<div className="flex-1">
												<h3 className="text-base md:text-lg font-semibold mb-1">
													{t('entities.schedule.lets_create_first')}
												</h3>
												<p className="text-xs md:text-sm text-muted-foreground">
													{t('entities.schedule.automate_music_description')}
												</p>
											</div>
											{canCreateSchedule && (
												<Button
													variant="default"
													className="rounded-full hidden sm:flex"
													onClick={handleCreateSchedule}
												>
													{t('entities.schedule.add_schedule')}
													<Plus className="ml-1 !h-5 !w-5" />
												</Button>
											)}
										</div>
									</div>
								)}

							{/* Assigned Schedules Carousel (In-Use) */}
							{(inUseSchedules.length > 0 || isLoadingInUse) && (
								<CarouselSection
									title={t('entities.schedule.assigned_schedules')}
									schedules={inUseSchedules}
									isLoading={isLoadingInUse}
									carouselRef={inUseCarouselRef}
									scrollState={inUseScrollState}
									onScrollStateChange={handleInUseScrollStateChange}
									onScheduleClick={onScheduleClick}
									onFavoriteClick={onFavoriteClick}
									loadingScheduleId={loadingScheduleId}
									onPageChange={onInUsePageChange}
									pagination={inUsePagination}
									favoriteScheduleIds={favoriteScheduleIds}
									showActions={true}
									onAssign={onAssign}
									onDuplicate={onDuplicate}
									onEdit={onEdit}
									onRemove={onRemove}
									viewAllUrl="/schedules/view/in-use"
								/>
							)}

							{/* Custom Schedules Carousel */}
							{(assignedSchedules.length > 0 || isLoadingAssigned) && (
								<CarouselSection
									title={t('entities.schedule.custom_schedules')}
									schedules={assignedSchedules}
									isLoading={isLoadingAssigned}
									carouselRef={assignedCarouselRef}
									scrollState={assignedScrollState}
									onScrollStateChange={handleAssignedScrollStateChange}
									onScheduleClick={onScheduleClick}
									onFavoriteClick={onFavoriteClick}
									loadingScheduleId={loadingScheduleId}
									onPageChange={onAssignedPageChange}
									pagination={assignedPagination}
									favoriteScheduleIds={favoriteScheduleIds}
									showActions={true}
									onAssign={onAssign}
									onDuplicate={onDuplicate}
									onEdit={onEdit}
									onRemove={onRemove}
									viewAllUrl="/schedules/view/assigned"
								/>
							)}

							{/* Favorites Carousel */}
							{(favoriteSchedules.length > 0 || isLoadingFavorites) && (
								<CarouselSection
									title={t('entities.music.favorites')}
									schedules={favoriteSchedules}
									isLoading={isLoadingFavorites}
									carouselRef={favoritesCarouselRef}
									scrollState={favoritesScrollState}
									onScrollStateChange={handleFavoritesScrollStateChange}
									onScheduleClick={onScheduleClick}
									onFavoriteClick={onFavoriteClick}
									loadingScheduleId={loadingScheduleId}
									isFavoritesCarousel={true}
									onPageChange={onFavoritesPageChange}
									pagination={favoritesPagination}
									favoriteScheduleIds={favoriteScheduleIds}
									showActions={true}
									onAssign={onAssign}
									onDuplicate={onDuplicate}
									onEdit={onEdit}
									onRemove={onRemove}
									viewAllUrl="/schedules/view/favorites"
								/>
							)}

							{/* Business Category Sections */}
							{filteredSections.map((section) => (
								<FilteredScheduleSection
									key={section.id}
									section={section}
									onScheduleClick={onScheduleClick}
									onFavoriteClick={onFavoriteClick}
									onLoadingStateChange={(isLoading: boolean) =>
										handleSectionLoadingChange(section.id, isLoading)
									}
									onAssign={onAssign}
								/>
							))}

							{hasMoreFilteredSections && (
								<div ref={observerTarget} className="w-full min-h-[100px]" />
							)}

							{loadingSectionsCount > 0 && (
								<div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[1000] bg-card border border-border rounded-lg px-4 py-2 shadow-lg flex items-center justify-center backdrop-blur-sm animate-[slideUp_0.3s_ease-out]">
									<div className="flex items-center gap-2">
										<div className="w-4 h-4 border-2 border-foreground/30 border-t-foreground rounded-full animate-spin" />
										<span className="text-foreground text-sm font-medium">
											{t('actions.loading')}
										</span>
									</div>
								</div>
							)}
						</>
					)}
				</div>
			</PageLayout>
			{/* Create/Edit Modal */}
			<Dialog open={isCreateModalOpen} onOpenChange={onCloseCreateModal}>
				<DialogContent
					className="sm:max-w-[600px] p-0 gap-0"
					showCloseButton={false}
				>
					<div className="px-6 py-6 border-b">
						<DialogTitle className="text-2xl">
							{editingScheduleId
								? t('entities.schedule.edit_schedule')
								: t('entities.schedule.create_schedule')}
						</DialogTitle>
					</div>

					<div className="px-6 py-6 space-y-6">
						<div className="space-y-2">
							<Label htmlFor="schedule-name" className="text-base font-medium">
								{t('entities.schedule.schedule_name_required')}
							</Label>
							<Input
								id="schedule-name"
								placeholder={t('entities.schedule.schedule_name_placeholder')}
								value={scheduleName}
								onChange={(e) => onScheduleNameChange(e.target.value)}
								className="h-12"
							/>
						</div>

						<div className="space-y-2">
							<Label
								htmlFor="schedule-description"
								className="text-base font-medium"
							>
								{t('entities.schedule.description')}
							</Label>
							<Textarea
								id="schedule-description"
								placeholder={t('entities.schedule.description_placeholder')}
								value={scheduleDescription}
								onChange={(e) => onScheduleDescriptionChange(e.target.value)}
								className="min-h-[120px] bg-background border-border text-foreground resize-none"
								maxLength={180}
							/>
							<p className="text-sm text-muted-foreground">
								{scheduleDescription.length}/180
							</p>
						</div>
					</div>

					{/* Footer */}
					<div className="px-6 py-6 border-t flex justify-end gap-3">
						<Button
							variant="outline"
							onClick={onCancelCreate}
							className="rounded-full px-6 cursor-pointer"
							disabled={isSubmittingSchedule}
						>
							{t('actions.cancel')}
						</Button>
						<Button
							variant="default"
							onClick={onCreateSchedule}
							className="rounded-full cursor-pointer px-6"
							disabled={!scheduleName.trim() || isSubmittingSchedule}
						>
							{isSubmittingSchedule ? (
								editingScheduleId ? (
									t('entities.schedule.updating')
								) : (
									t('entities.schedule.creating')
								)
							) : editingScheduleId ? (
								<>
									{t('entities.schedule.update')}
									<Plus className="ml-1.5 !h-4 !w-4" />
								</>
							) : (
								<>
									{t('actions.continue')}
									<Plus className="ml-1.5 !h-4 !w-4" />
								</>
							)}
						</Button>
					</div>
				</DialogContent>
			</Dialog>

			{/* Assign Schedule Modal */}
			<AssignScheduleModal
				open={isAssignModalOpen}
				onOpenChange={onAssignModalChange}
				scheduleId={selectedScheduleForAssign?.id}
				scheduleName={selectedScheduleForAssign?.name}
			/>

			{/* Remove Schedule Modal */}
			<RemoveScheduleModal
				open={isRemoveModalOpen}
				onOpenChange={onRemoveModalChange}
				scheduleId={selectedScheduleForRemove?.id}
				scheduleName={selectedScheduleForRemove?.name}
				scheduleDescription={selectedScheduleForRemove?.description}
			/>

			{/* Duplicate Schedule Modal */}
			<DuplicateScheduleModal
				open={isDuplicateModalOpen}
				onOpenChange={onDuplicateModalChange}
				scheduleId={selectedScheduleForDuplicate?.id}
				scheduleName={selectedScheduleForDuplicate?.name}
				scheduleDescription={selectedScheduleForDuplicate?.description}
			/>

			<DesktopOnlyModal
				visible={showDesktopOnlyWarning}
				onClose={() => setShowDesktopOnlyWarning(false)}
			/>
		</>
	)
}

export default SchedulesListPageUI
